import type { PaddleEventData } from "@paddle/paddle-js";
import { computed, reactive } from "vue";
import { defineStore } from "pinia";

import {
  buildPaddlePaymentMethod,
  type SubscriptionPaymentMethod,
} from "@Modules/billing/Data/Models/SubscriptionPaymentMethod";

interface XPaymentMethodState {
  /**
   * The subscription whose payment method is being updated in the
   * currently open Paddle checkout, if any.
   */
  pendingSubscriptionId: string | null

  /**
   * The subscription whose payment method was just updated through a
   * completed Paddle checkout, if any.
   */
  updatedSubscriptionId: string | null

  /**
   * The payment method the completed Paddle checkout went through, if any.
   * It stays null when the checkout carries no payment method.
   */
  updatedPaymentMethod: SubscriptionPaymentMethod | null
}

/**
 * Bridges the global Paddle checkout events (`usePaddle`) with the billing page
 * that started a payment method update, so the page can show its confirmation badge.
 */
export const XPaymentMethodStore = defineStore("x-payment-method", () => {
  //
  // Data
  const state = reactive<XPaymentMethodState>({
    pendingSubscriptionId: null,
    updatedSubscriptionId: null,
    updatedPaymentMethod: null,
  });

  //
  // Computed
  const pendingSubscriptionId = computed<string | null>(() => state.pendingSubscriptionId);
  const updatedSubscriptionId = computed<string | null>(() => state.updatedSubscriptionId);
  const updatedPaymentMethod = computed<SubscriptionPaymentMethod | null>(() => state.updatedPaymentMethod);

  //
  // Methods

  /**
   * Marks the given subscription as awaiting a payment method update.
   * Call it right before opening the Paddle checkout.
   */
  function beginPaymentMethodUpdate(subscriptionId: string): void {
    state.pendingSubscriptionId = subscriptionId;
  }

  /**
   * Promotes the pending subscription to updated and keeps the payment method the customer
   * just went through.
   * Called by the global Paddle event callback on "checkout.completed".
   */
  function completePaymentMethodUpdate(checkoutResponse?: PaddleEventData): void {
    state.updatedSubscriptionId = state.pendingSubscriptionId;
    state.pendingSubscriptionId = null;
    state.updatedPaymentMethod = buildPaddlePaymentMethod(checkoutResponse);
  }

  /**
   * Discards the pending payment method update.
   * Called when the Paddle checkout is closed manually by the user or fails to open.
   */
  function abortPaymentMethodUpdate(): void {
    state.pendingSubscriptionId = null;
  }

  /**
   * Clears the updated subscription once the UI has finished reacting to it.
   */
  function resetPaymentMethodUpdate(): void {
    state.updatedSubscriptionId = null;
    state.updatedPaymentMethod = null;
  }

  return {
    pendingSubscriptionId,
    updatedSubscriptionId,
    updatedPaymentMethod,
    beginPaymentMethodUpdate,
    completePaymentMethodUpdate,
    abortPaymentMethodUpdate,
    resetPaymentMethodUpdate,
  };
});

export type PaymentMethodStore = ReturnType<typeof XPaymentMethodStore>

export default XPaymentMethodStore;
